
import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { BehaviorSubject, Subscription, combineLatest, filter, take, skip } from 'rxjs';
import * as am4core from '@amcharts/amcharts4/core';

import { SearchCriteriaSortFieldEnum } from '@api/mentions/enum/search-criteria-sort-field.enum';
import { MentionSearchFacetTypesEnum } from '@api/mentions/enum/mention-search-facet-types.enum';

import { CardChartComponentHighlightColorEnum, CardChartComponentLayoutEnum } from '@Common/Charts/components/CardChart/card-chart.component';
import { chartColors } from '@Common/Charts/enums/chart-colors.enum';
import { AbstractChartOptionsModel } from '@Common/Charts/models/abstract-chart-options.model';
import { StackTypesEnum } from '@Common/Charts/models/barChart/category-value-x-y-chart-options.model';
import { ChartDisplaySettingsModel } from '@Common/Charts/models/chart-display-settings.model';
import { ChartMetadataModel } from '@Common/Charts/models/chart-metadata.model';
import { ChartStylesModel } from '@Common/Charts/models/chart-styles.model';
import { ChartSpecificTypesEnum } from '@Common/Reports/enums/chart-specific-types.enum';
import { ReportBlockTypeEnum } from '@Common/Reports/enums/report-block-type.enum';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { ReportDateRangeModel } from '@Common/Reports/models/report-date-range.model';
import { ReportsChartDetailService } from '@Common/Reports/services/reports-chart-detail.service';
import { ReportsChartSeriesConverterService } from '@Common/Reports/services/reports-chart-series-converter.service';
import { ChartModel } from '@Common/Charts/models/chart.model';
import { ChartGroupInputModel } from '@Common/Charts/models/chart-group-input.model';
import { MultiSeriesPieChartModel } from '@Common/Charts/models/pieChart/multiSeriesPieChart/multi-series-pie-chart.model';
import { ChartLoadingOptionsEnum } from '@Common/Charts/enums/chart-loading-options.enum';
import { MultiSeriesPieChartOptionsModel } from '@Common/Charts/models/pieChart/multiSeriesPieChart/multi-series-pie-chart-options.model';
import { LabelDisplayOptionsEnum } from '@Common/Misc/enums/label-display-options.enum';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { UpsellDialogType, UpsellInfoDialogComponent } from '@Common/Dialogs/components/UpsellInfoDialog/upsell-info-dialog.component';
import { Am5DateValueXYChart } from '@Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { Am5ChartTooltipService } from '@Common/Charts/services/am5-chart-tooltip.service';
import { Am5ChartConfigurationService } from '@Common/Reports/services/am5-chart-configuration.service';
import { Am5ChartSettingsService } from '@Common/Reports/services/am5-chart-settings.service';
import { Am5Chart } from '@Common/Charts/models/am5-chart.model';
import { ChartDisplayElementModel } from '@Common/Reports/models/chart-display-element.model';
import { reportBlockTypesMap } from '@Common/Reports/models/reports-block-types.model';
import { BlockModel } from '@Common/PageBuilder/models/block.model';

import { userAppPreferencesDefault } from '@Core/Users/data/user-app-preferences-default';
import { socialNetworkTypeColorMap } from '@Core/Users/data/social-network-type-color-map';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { UserCurrency } from '@Core/Users/enums/user-currency';

import { MonitoringDashboardSearchService } from '@Monitor/Core/services/monitoring-dashboard-search.service';
import { MADSocialSeriesTypesEnum } from '@Monitor/Shared/enums/mad-series-types.enum';
import { MediaTypeModel } from '@Monitor/Shared/models/media-type.model';
import { MADSeriesMap } from '@Monitor/Shared/services/mad-charts.service';

import { SocialSeriesMetricsEnum } from '@Social/Intelligence/enums/social-series-metrics.enum';
import { SocialChartTooltipService } from '@Social/Shared/services/social-chart-tooltip.service';
import { NetworkTypeEnum } from '@Social/Core/enums/network-type.enum';
import { networkTypeToMediaTypeModelMap } from '@Social/Intelligence/models/network-type-to-media-type-model.map';
import { SocialChartExportModel, SocialChartExportService } from '@Social/Intelligence/services/social-chart-export.service';
import { TopTermsComponent, TopTermsTitleConfig } from '@Social/Shared/components/TopTerms/top-terms.component';
import { TopPostsComponent } from '@Social/Shared/components/TopPosts/top-posts.component';

import { MADSocialService } from './mad-social.service';
import { SearchCriteriaQuerySentimentsEnum } from '@api/mentions/enum/search-criteria-query-sentiments.enum';
import { ChartTimeIntervalsEnum } from '@Common/Reports/enums/chart-time-intervals.enum';
import { StackedChartTypesEnum } from '@Common/Reports/enums/stacked-chart-types.enum';
import { stackedBarColorsConfig } from '@Common/Charts/config/stacked-bar-colors.config';
import { ChartExportEventModel } from '@Common/Charts/models/chart-export-event.model';

import { MonitorConfigService } from '@Common/MonitorShared/services/monitor-config.service';
import { mediaTypeToNetworkTypeMap } from '@Social/Intelligence/models/network-type-to-media-type-model.map';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';


@Component({
    selector: 'agpr-mad-social',
    templateUrl: './mad-social.component.html',
    styleUrls: ['./mad-social.component.scss'],
    providers: [
        Am5ChartTooltipService,
        {
            // is needed for topterms, we had to Inject 'SocialSeriesService' as AbstractDashboardSearchService
            // and we have to prodive it in the componened to it overides the 'SocialSeriesService' from the social module
            provide: 'SocialSeriesService',
            useExisting: MonitoringDashboardSearchService
        },
    ],
    standalone: false
})
export class MADSocialComponent implements OnInit, OnDestroy, AfterViewInit {
    readonly id: string = 'mad_social';
    readonly socialMetrics = SocialSeriesMetricsEnum;
    readonly cardChartComponentHighlightColors = CardChartComponentHighlightColorEnum;
    readonly cardChartLayoutTypes = CardChartComponentLayoutEnum;
    readonly chartLoadingOptionsEnum = ChartLoadingOptionsEnum;
    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;
    readonly mentionSearchFacetTypesEnum = MentionSearchFacetTypesEnum;
    readonly stackTypes = StackTypesEnum;
    readonly sentimentsToChartColorsMap = new Map<SearchCriteriaQuerySentimentsEnum, chartColors>([
        [SearchCriteriaQuerySentimentsEnum.positive, chartColors.agprPositive],
        [SearchCriteriaQuerySentimentsEnum.neutral, chartColors.agprNeutral],
        [SearchCriteriaQuerySentimentsEnum.negative, chartColors.agprNegative],
        [SearchCriteriaQuerySentimentsEnum.notAvailable, chartColors.agprNotAvailable]
    ]);
    readonly mostUsedTopTermsConfig = {
        mainTitle: {
            mode: 'single',
            title: 'Social.intelligence.charts.topTerms.heading.mostUsed',
        },
        subTitle: {
            mode: 'static',
            title: 'Social.intelligence.charts.topTerms.heading.mostUsedTerms'
        }
    } as TopTermsTitleConfig;
    readonly buttonTypes = ButtonTypesEnum;
    readonly madSocialChartTypeEnum = ReportBlockTypeEnum;
    readonly supportedExportFormats = [ 'png', 'jpg', 'svg', 'json', 'csv', 'print' ];

    @Input() isAccountSearch: boolean = false;
    @Input() isSavedCoverage: boolean = false;

    @Output() reportSeriesReady: EventEmitter<MADSeriesMap> = new EventEmitter<MADSeriesMap>();
    @Output() reportSeriesChange: EventEmitter<{oldSeriesTypes: any[]; newSeries: ReportSeriesModel[]}> = new EventEmitter();

    seriesMap: MADSeriesMap;
    hasInitializedSeries: boolean = false;
    classToChartModelMap: Map<string, ChartModel | ChartGroupInputModel | Am5Chart>;
    classToReportSeriesMap: Map<string, ReportSeriesModel[]>;
    currencyCode: UserCurrency;
    currentSocialType: NetworkTypeEnum = NetworkTypeEnum.all;
    noContentLabel: string;
    noContentLabelText: string;
    hasSocial: boolean = true;
    overlayContainer: HTMLElement;
    summaryTrendLineChartNoData: boolean = false;
    private viewInitted$ = new BehaviorSubject<boolean>(false);
    private seriesMapProcessed$ = new BehaviorSubject<boolean>(false);
    public shareOfVoiceBySocialTopic: ChartGroupInputModel;
    public sentimentSummaryChart: MultiSeriesPieChartModel;

    availableNetworkTypes: NetworkTypeEnum[] = [
        NetworkTypeEnum.all,
    ];
    availableSocialMediaTypeModels: MediaTypeModel[] = [];

    // Cards
    totalVolumeCardSeries: ReportSeriesModel[] = [];
    totalVolumeCardSeriesTypes = [MADSocialSeriesTypesEnum.totalVolumeCard, MADSocialSeriesTypesEnum.totalVolumeCardPreviousPeriod];
    totalImpressionCardSeries: ReportSeriesModel[] = [];
    totalImpressionCardSeriesTypes = [MADSocialSeriesTypesEnum.totalImpressionCard, MADSocialSeriesTypesEnum.totalImpressionCardPreviousPeriod];
    totalEngagementCardSeries: ReportSeriesModel[] = [];
    totalEngagementCardSeriesTypes = [MADSocialSeriesTypesEnum.totalEngagementCard, MADSocialSeriesTypesEnum.totalEngagementCardPreviousPeriod];

    // Summary By Social Network
    summaryTrendLineChart: Am5DateValueXYChart;
    summaryTrendLineChartClass: string = 'agpr-madSocial-summaryTrendLineChart';
    summaryTrendLineSeries: ReportSeriesModel[];
    summaryTotalVolumeCardSeries: ReportSeriesModel[] = [];
    summaryTotalVolumeCardSeriesTypes = [
        MADSocialSeriesTypesEnum.summaryTotalVolumeCard, MADSocialSeriesTypesEnum.summaryTotalVolumeCardPreviousPeriod
    ];
    summaryTotalImpressionCardSeries: ReportSeriesModel[] = [];
    summaryTotalImpressionCardSeriesTypes = [
        MADSocialSeriesTypesEnum.summaryTotalImpressionCard, MADSocialSeriesTypesEnum.summaryTotalImpressionCardPreviousPeriod
    ];
    summaryTotalEngagementCardSeries: ReportSeriesModel[] = [];
    summaryTotalEngagementCardSeriesTypes = [
        MADSocialSeriesTypesEnum.summaryTotalEngagementCard, MADSocialSeriesTypesEnum.summaryTotalEngagementCardPreviousPeriod
    ];

    // Social Network Comparison
    shareOfVoiceBySocialNetwork: ChartGroupInputModel;
    shareOfVoiceBySocialNetworkClass: string = 'agpr-madSocial-shareOfVoiceBySocialNetwork';
    shareOfVoiceBySocialNetworkSubCharts: MultiSeriesPieChartModel[];
    shareOfVoiceBySocialNetworkLoading: boolean;
    shareOfVoiceBySocialNetworkSubscription: Subscription;

    shareOfVoiceBySocialNetworkVolumeSeries: ReportSeriesModel[] = [];
    shareOfVoiceBySocialNetworkVolumeSeriesTypes = [
        MADSocialSeriesTypesEnum.totalVolumePiePreviousPeriod, MADSocialSeriesTypesEnum.totalVolumePie
    ];
    shareOfVoiceBySocialNetworkImpressionsSeries: ReportSeriesModel[] = [];
    shareOfVoiceBySocialNetworkImpressionsSeriesTypes = [
        MADSocialSeriesTypesEnum.totalImpressionPiePreviousPeriod, MADSocialSeriesTypesEnum.totalImpressionPie
    ];
    shareOfVoiceBySocialNetworkEngagementsSeries: ReportSeriesModel[] = [];
    shareOfVoiceBySocialNetworkEngagementsSeriesTypes = [
        MADSocialSeriesTypesEnum.totalEngagementPiePreviousPeriod, MADSocialSeriesTypesEnum.totalEngagementPie
    ];
    shareOfVoiceBySocialNetworkChartOptions: AbstractChartOptionsModel = {};
    shareOfVoiceBySocialNetworkChartMetadata: ChartMetadataModel;

    // Social Sentiment Trend
    sentimentTrendChart: Am5DateValueXYChart;
    sentimentTrendSeries: ReportSeriesModel[];
    sentimentTrendChartNoData: boolean = false;
    sentimentTrendChartClass: string = 'agpr-madSocial-sentimentTrendChart';
    selectedSentimentTrendChartTabIndex: number = 0;


    // Most Used Hastags/Handles
    mostUsedTopTermsSeries: ReportSeriesModel;

    // Top Posts
    topPostsSeries: ReportSeriesModel;
    topPostsSortField: SearchCriteriaSortFieldEnum;

    subscriptions: Subscription[] = [];

    @ViewChild('topTermChart') topTermChart: TopTermsComponent;
    @ViewChild('topPostsChart') topPostsChart: TopPostsComponent;

    constructor(
        public madSocialService: MADSocialService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsChartDetailService: ReportsChartDetailService,
        public dashboardSearchService: MonitoringDashboardSearchService,
        public localizationService: LocalizationService,
        public socialChartTooltipService: SocialChartTooltipService,
        public currentUserStateService: CurrentUserStateService,
        public coreDialogService: CoreDialogService,
        public chartExportService: SocialChartExportService,
        public monitorConfigService: MonitorConfigService,
        public am5ChartConfigurationService: Am5ChartConfigurationService,
        public am5ChartSettingsService: Am5ChartSettingsService,
        public am5ChartTooltipService: Am5ChartTooltipService
    ) {
        this.noContentLabelText = this.localizationService.transform('Social.intelligence.wordCloudChart.noContent');
        this.noContentLabel = `<span style='font-size: 0.875rem;'>${this.noContentLabelText}</span>`;
    }

    // the view mention link is in amcharts, and we couldn't use our normal way to bind them
    @HostListener('click', ['$event.target']) onClick(event) {
        if (event?.classList?.contains('viewMentionLink')) {
            let chartDiv = event;
            while (chartDiv && !chartDiv.classList.contains('agpr-mad-chartAnchor')) {
                chartDiv = chartDiv.parentElement;
            }
            if (!chartDiv) {
                return;
            }
            const chartModel = this.classToChartModelMap.get(chartDiv.classList[1]);
            if (chartModel) {
                this.reportsChartDetailService.viewCoverageAnalysisDialog(event.href, null, chartModel?.clone(), true);
            }
            return false;
        }
    }

    openAm5ChartDrilldown(event: any): void {
        const url = event.data;
        if (url) {
            let chartDiv = event.element;
            while (chartDiv && !chartDiv.classList.contains('agpr-mad-chartAnchor')) {
                chartDiv = chartDiv.parentElement;
            }
            if (!chartDiv) {
                return;
            }
            const chartModel = this.classToChartModelMap.get(chartDiv.classList[1]);
            if (chartModel) {
                this.reportsChartDetailService.viewCoverageAnalysisDialog(
                    url,
                    null,
                    chartModel.clone(),
                    true,
                    null,
                    null,
                    this.classToReportSeriesMap.get(chartDiv.classList[1])
                );
            }
        }
    }

    ngOnInit(): void {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            this.madSocialService.getSeriesMap().pipe(filter(x => !!x)).subscribe((seriesMap) => {
                seriesMap.forEach(series => series.mentions.filters.searchCriteria.query.includedMediaTypes = MediaTypeModel.getSocialMediaTypes());
                this.seriesMap = seriesMap;

                // Cards
                this.setupCardSeries();

                // Summary By Social Network
                this.setupSummarySeries();

                // Social Network Comparison
                this.setupNetworkComparisonSeries();

                // Social Sentiment Trend
                this.setupSentimentSeries();

                // Social Top Terms
                this.mostUsedTopTermsSeries = this.seriesMap.get(MADSocialSeriesTypesEnum.topTerms);
                if (this.topTermChart) {
                    if (this.topTermChart.getMediaTypesFromSelectedType()) {
                        this.mostUsedTopTermsSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
                            this.topTermChart.getMediaTypesFromSelectedType();
                    } else {
                        this.mostUsedTopTermsSeries.mentions.filters.searchCriteria.query.includedMediaTypes = MediaTypeModel.getSocialMediaTypes();
                    }
                    this.topTermChart.updateSeriesOnMetricChange(this.mostUsedTopTermsSeries);
                }

                // Social Top Posts
                this.topPostsSeries = this.seriesMap.get(MADSocialSeriesTypesEnum.topPosts);
                if (this.topPostsChart) {
                    this.topPostsSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
                        networkTypeToMediaTypeModelMap.get(this.topPostsChart.currentSocialType);
                    this.topPostsSeries.mentions.mentionRequestGenerator = () => {
                        return this.topPostsChart.currentSort.value;
                    };
                }

                this.refreshClassToChartModelMap();
                this.seriesMapProcessed$.next(true);
            }),
            combineLatest([
                this.seriesMapProcessed$,
                this.viewInitted$
            ]).subscribe(() => {
                // HACK: the top terms triggers a fullfill series twice for the WordCloudSeries
                // which causes problems with the previous data cards
                setTimeout(() => {
                    this.reportSeriesReady.emit(this.seriesMap);
                }, 200);
            }),
            this.currentUserStateService.getPermissions().subscribe(permissions => {
                if (permissions) {
                    this.hasSocial = permissions.includes(userPermissionsEnum.socialIntelligence);
                }
            }),
            this.monitorConfigService.getAvailableSocialMediaTypes().subscribe(socialMediaTypes => {
                socialMediaTypes.forEach(type => {
                    if (mediaTypeToNetworkTypeMap.has(type)) {
                        this.availableNetworkTypes.push(mediaTypeToNetworkTypeMap.get(type));
                    }
                });

                if (socialMediaTypes.length === 0) {
                    this.availableNetworkTypes = [];
                }
            }),
            this.currentUserStateService.getUserCurrency().subscribe(x => this.currencyCode = x),
            // rebuild once the real date query is known/changes, see setupSentimentSeries()/setupSummarySeries()
            this.dashboardSearchService.getDateQuery().pipe(skip(1)).subscribe(() => {
                if (this.seriesMap) {
                    this.setupSummarySeries();
                    this.setupSentimentSeries();
                    this.refreshClassToChartModelMap();
                }
            })
        );

    }

    ngAfterViewInit(): void {
        this.classToChartModelMap = new Map<string, ChartModel | ChartGroupInputModel | Am5Chart>([
            [this.summaryTrendLineChartClass, this.summaryTrendLineChart],
            [this.shareOfVoiceBySocialNetworkClass, this.shareOfVoiceBySocialNetwork],
            [this.sentimentTrendChartClass, this.sentimentTrendChart]
        ]);
        this.classToReportSeriesMap = new Map<string, ReportSeriesModel[]>([
            [this.summaryTrendLineChartClass, this.summaryTrendLineSeries],
            [this.sentimentTrendChartClass, this.sentimentTrendSeries]
        ]);
        if (this.topTermChart.getMediaTypesFromSelectedType()) {
            this.mostUsedTopTermsSeries.mentions.filters.searchCriteria.query.includedMediaTypes = this.topTermChart.getMediaTypesFromSelectedType();
        } else {
            this.mostUsedTopTermsSeries.mentions.filters.searchCriteria.query.includedMediaTypes = MediaTypeModel.getSocialMediaTypes();
        }
        this.topTermChart.updateSeriesOnMetricChange(this.mostUsedTopTermsSeries);
        this.topPostsSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.topPostsChart.currentSocialType);
        this.topPostsSeries.mentions.mentionRequestGenerator = () => {
            return this.topPostsChart.currentSort.value;
        };
        this.viewInitted$.next(true);
    }

    private setupCardSeries(): void {
        this.totalVolumeCardSeries = this.totalVolumeCardSeriesTypes.map(x => this.seriesMap.get(x));
        this.totalImpressionCardSeries = this.totalImpressionCardSeriesTypes.map(x => this.seriesMap.get(x));
        this.totalEngagementCardSeries = this.totalEngagementCardSeriesTypes.map(x => this.seriesMap.get(x));
    }

    private setupSummarySeries(): void {
        this.summaryTrendLineSeries = [this.seriesMap.get(MADSocialSeriesTypesEnum.summaryTrend)];
        this.dashboardSearchService.getDateQuery().pipe(take(1)).subscribe(dateQuery => {
            this.summaryTrendLineSeries[0].dateRange = new ReportDateRangeModel().setFromDateQuery(dateQuery);
        });
        const summaryTrendLineStyles = new ChartStylesModel();
        summaryTrendLineStyles.colorMap = {
            'analysisColor1': chartColors.agprBlue,
            'analysisColor2': chartColors.agprPurple,
            'analysisColor3': chartColors.agprLightBlue
        };
        const chartSeries = this.reportsChartSeriesConverterService.getDateValueChartSeries(
            this.summaryTrendLineSeries, ReportBlockTypeEnum.COVERAGE_OVER_TIME, ChartSpecificTypesEnum.LINE, this.currencyCode
        );
        this.summaryTrendLineSeries[0].setChartType(ReportBlockTypeEnum.COVERAGE_OVER_TIME);
        const chartDisplayElement = new ChartDisplayElementModel('', this.summaryTrendLineSeries, summaryTrendLineStyles);
        chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
        chartDisplayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;
        const chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(this.id, ReportBlockTypeEnum.COVERAGE_OVER_TIME);
        chartConfiguration.noContent.isMessageStyle = true;
        const chartSettings = this.am5ChartSettingsService.getChartSettings(
            ReportBlockTypeEnum.COVERAGE_OVER_TIME,
            chartDisplayElement
        );
        chartSeries.forEach((x, i) => {
            x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip([x], chartConfiguration, chartSettings, i, false);
        });
        this.summaryTrendLineChart = new Am5DateValueXYChart(
            chartSeries,
            chartConfiguration,
            chartSettings
        );

        this.summaryTotalVolumeCardSeries = this.summaryTotalVolumeCardSeriesTypes.map(x => this.seriesMap.get(x));
        this.summaryTotalImpressionCardSeries = this.summaryTotalImpressionCardSeriesTypes.map(x => this.seriesMap.get(x));
        this.summaryTotalEngagementCardSeries = this.summaryTotalEngagementCardSeriesTypes.map(x => this.seriesMap.get(x));

        this.setSummaryTrendIncludedMediaTypes(this.summaryTrendLineSeries[0]);
    }

    private setupNetworkComparisonSeries(): void {
        this.shareOfVoiceBySocialNetworkVolumeSeries = this.shareOfVoiceBySocialNetworkVolumeSeriesTypes.map(x => this.seriesMap.get(x));
        this.shareOfVoiceBySocialNetworkImpressionsSeries = this.shareOfVoiceBySocialNetworkImpressionsSeriesTypes.map(x => this.seriesMap.get(x));
        this.shareOfVoiceBySocialNetworkEngagementsSeries = this.shareOfVoiceBySocialNetworkEngagementsSeriesTypes.map(x => this.seriesMap.get(x));

        this.shareOfVoiceBySocialNetworkSubCharts = [];
        this.shareOfVoiceBySocialNetworkSubCharts.push(
            new MultiSeriesPieChartModel(
                this.shareOfVoiceBySocialNetworkVolumeSeries,
                this.getPieChartChartOptions(),
                this.getPieChartMetaData(
                    this.localizationService.transform(`Monitor.MAD.social.${MADSocialSeriesTypesEnum.totalVolumePie}.label`)
                )
            )
        );
        this.shareOfVoiceBySocialNetworkSubCharts.push(
            new MultiSeriesPieChartModel(
                this.shareOfVoiceBySocialNetworkImpressionsSeries,
                this.getPieChartChartOptions(),
                this.getPieChartMetaData(
                    this.localizationService.transform(`Monitor.MAD.social.${MADSocialSeriesTypesEnum.totalImpressionPie}.label`)
                )
            )
        );
        this.shareOfVoiceBySocialNetworkSubCharts.push(
            new MultiSeriesPieChartModel(
                this.shareOfVoiceBySocialNetworkEngagementsSeries,
                this.getPieChartChartOptions(),
                this.getPieChartMetaData(
                    this.localizationService.transform(`Monitor.MAD.social.${MADSocialSeriesTypesEnum.totalEngagementPie}.label`)
                )
            )
        );

        /**
         *  leaving this here because we eventually want to use PieChart.. but because this is using chart group, there is much work to be done for it to work
         *  fix with NEP-30093
         *
            const pieChartBlock = new BlockModel();
            pieChartBlock.type = new BlockTypeModel(ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK, null, null, null, null, null);
            pieChartBlock.displayElement = new ChartDisplayElementModel('', null, null);
            (pieChartBlock.displayElement as ChartDisplayElementModel).chartSpecificType = ChartSpecificTypesEnum.PIE;
            (pieChartBlock.displayElement as ChartDisplayElementModel).chartHeaderSettings = new ChartDisplaySettingsModel({previousPeriod: true});

            this.shareOfVoiceBySocialNetworkSubCharts.push(
                new PieChart(
                    this.shareOfVoiceBySocialNetworkVolumeSeries,
                    this.getPieChartChartOptions(),
                    this.getPieChartMetaData(
                        this.localizationService.transform(`Monitor.MAD.social.${MADSocialSeriesTypesEnum.totalVolumePie}.label`)
                    ),
                    this.reportsChartSeriesConverterService.getChartSeries(this.shareOfVoiceBySocialNetworkVolumeSeries, pieChartBlock)
                )
            );
            this.shareOfVoiceBySocialNetworkSubCharts.push(
                new PieChart(
                    this.shareOfVoiceBySocialNetworkImpressionsSeries,
                    this.getPieChartChartOptions(),
                    this.getPieChartMetaData(
                        this.localizationService.transform(`Monitor.MAD.social.${MADSocialSeriesTypesEnum.totalImpressionPie}.label`)
                    ),
                    this.reportsChartSeriesConverterService.getChartSeries(this.shareOfVoiceBySocialNetworkImpressionsSeries, pieChartBlock)
                )
            );
            this.shareOfVoiceBySocialNetworkSubCharts.push(
                new PieChart(
                    this.shareOfVoiceBySocialNetworkEngagementsSeries,
                    this.getPieChartChartOptions(),
                    this.getPieChartMetaData(
                        this.localizationService.transform(`Monitor.MAD.social.${MADSocialSeriesTypesEnum.totalEngagementPie}.label`)
                    ),
                    this.reportsChartSeriesConverterService.getChartSeries(this.shareOfVoiceBySocialNetworkEngagementsSeries, pieChartBlock)
                )
            );
        */

        this.shareOfVoiceBySocialNetworkChartOptions = this.getShareOfVoiceBySocialChartOptions();

        this.shareOfVoiceBySocialNetworkChartMetadata = {
            type: null,
            titleText: this.localizationService.transform('Social.intelligence.themeAnalytics.summary.shareOfVoiceBySocialNetwork'),
            dateRangeLabelText: null,
            labelDisplayOptions: [
                LabelDisplayOptionsEnum.percent
            ],
            noContentLabel: this.noContentLabel,
            enableDataDisplaySettings: false
        } as ChartMetadataModel;

        this.shareOfVoiceBySocialNetwork = new ChartGroupInputModel(
            this.shareOfVoiceBySocialNetworkSubCharts,
            this.shareOfVoiceBySocialNetworkChartOptions,
            this.shareOfVoiceBySocialNetworkChartMetadata
        );
    }

    private getPieChartChartOptions(): MultiSeriesPieChartOptionsModel {
        const socialTopicSubChartOptionsStyles = new ChartStylesModel();
        socialTopicSubChartOptionsStyles.titleFontSize = 18;
        socialTopicSubChartOptionsStyles.titleAlignment = 'middle';
        socialTopicSubChartOptionsStyles.innerRadiusPercent = 15;
        socialTopicSubChartOptionsStyles.seriesRadiusPercent = [35, 60];
        socialTopicSubChartOptionsStyles.titleLabelColor = this.defaultChartColors.otherColors.textColor;
        socialTopicSubChartOptionsStyles.colorMap = socialNetworkTypeColorMap;

        return {
            styles: socialTopicSubChartOptionsStyles,
            tooltipHTML: this.socialChartTooltipService.getTooltipHTML(
                'shareOfVoiceBySocialNetwork',
                '{category}{labelInTooltip}',
                '{value.value} ({value.percent.formatNumber(\'#.0\')}%)',
                null,
                'fa-solid fa-square'
            ),
            isSocialStyles: true
        };
    }

    private getShareOfVoiceBySocialChartOptions(): AbstractChartOptionsModel {
        const chartGroupStyles = new ChartStylesModel();
        chartGroupStyles.chartHeight = 360;
        chartGroupStyles.legendItemHorizontalPadding = 15;
        chartGroupStyles.titleLabelColor = this.defaultChartColors.otherColors.textColor;
        return {
            styles: chartGroupStyles,
            noPadding: true,
            customLegendItems: [
                {
                    name: this.localizationService.transform(`MediaType.${MediaTypeEnum.TWITTER}`),
                    fill: this.defaultChartColors.socialNetworkColors.twitter
                },
                {
                    name: this.localizationService.transform(`MediaType.${MediaTypeEnum.FACEBOOK}`),
                    fill: this.defaultChartColors.socialNetworkColors.facebook
                },
                {
                    name:this.localizationService.transform(`MediaType.${MediaTypeEnum.INSTAGRAM}`),
                    fill: this.defaultChartColors.socialNetworkColors.instagram
                },
                {
                    name: this.localizationService.transform(`MediaType.${MediaTypeEnum.YOUTUBE}`),
                    fill: this.defaultChartColors.socialNetworkColors.youtube
                },
                {
                    name: this.localizationService.transform(`MediaType.${MediaTypeEnum.REDDIT}`),
                    fill: this.defaultChartColors.socialNetworkColors.reddit
                },
                {
                    name: this.localizationService.transform(`MediaType.${MediaTypeEnum.LINKEDIN}`),
                    fill: this.defaultChartColors.socialNetworkColors.linkedin
                },
                {
                    name: this.localizationService.transform(`MediaType.${MediaTypeEnum.TIKTOK}`),
                    fill: this.defaultChartColors.socialNetworkColors.tiktok
                },
                {
                    name: this.localizationService.transform(`MediaType.${MediaTypeEnum.BLUESKY}`),
                    fill: this.defaultChartColors.socialNetworkColors.bluesky
                }
            ],
            isSocialStyles: true
        };
    }

    private getPieChartMetaData(titleText: string): ChartMetadataModel {
        return {
            titleText: titleText,
            // eslint-disable-next-line max-len
            labelDisplayOptions: [
                'percent'
            ],
            noContentLabel: this.noContentLabel,
            noDataFromDateRangeLabel: this.localizationService.transform('Social.intelligence.themeAnalytics.chart.noDataFromDateRange')
        } as ChartMetadataModel;
    }

    setupSentimentSeries(): void {
        const sentimentTrendChartSeries = this.seriesMap.get(MADSocialSeriesTypesEnum.sentimentTrend);
        this.dashboardSearchService.getDateQuery().pipe(take(1)).subscribe(dateQuery => {
            sentimentTrendChartSeries.dateRange = new ReportDateRangeModel().setFromDateQuery(dateQuery);
        });
        const sentimentTypes = [
            SearchCriteriaQuerySentimentsEnum.positive,
            SearchCriteriaQuerySentimentsEnum.neutral,
            SearchCriteriaQuerySentimentsEnum.notAvailable,
            SearchCriteriaQuerySentimentsEnum.negative
        ];
        sentimentTrendChartSeries.sentiments = sentimentTypes;
        const sentimentColors = {};
        sentimentTypes.forEach(color => {
            sentimentColors[color] = this.sentimentsToChartColorsMap.get(color) || stackedBarColorsConfig[color];
        });
        sentimentTrendChartSeries.styles.colorMap = sentimentColors;
        this.sentimentTrendSeries = [sentimentTrendChartSeries];

        const stackedChartTypes = this.selectedSentimentTrendChartTabIndex === 0
            ? StackedChartTypesEnum.PERCENTAGE
            : StackedChartTypesEnum.VOLUME;

        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap = sentimentColors;
        chartStyles.chartDisplaySettings = new ChartDisplaySettingsModel({
            title: false,
            dateRange: false,
            sourceLink: false,
            previousPeriod: false,
            multiSource: false,
            trendIndicators: false
        });
        const chartDisplayElement = new ChartDisplayElementModel('', this.sentimentTrendSeries, chartStyles);
        chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
        chartDisplayElement.stackedChartTypes = stackedChartTypes;
        chartDisplayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;

        const sentimentTrendBlock = new BlockModel(
            null,
            reportBlockTypesMap.get(ReportBlockTypeEnum.SENTIMENT_TREND),
            chartDisplayElement
        );
        const chartSeries = this.reportsChartSeriesConverterService.getChartSeries(this.sentimentTrendSeries, sentimentTrendBlock);
        const chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(
            this.id + '_sentimentTrend', ReportBlockTypeEnum.SENTIMENT_TREND, chartDisplayElement
        );
        chartConfiguration.header.hasHeader = false;
        chartConfiguration.noContent.isMessageStyle = true;
        const chartSettings = this.am5ChartSettingsService.getChartSettings(
            ReportBlockTypeEnum.SENTIMENT_TREND, chartDisplayElement
        );
        this.am5ChartTooltipService.setDoLink(true);
        chartSeries.forEach((x, i) => {
            x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip(
                chartSeries, chartConfiguration, chartSettings, i, false
            );
        });
        this.sentimentTrendChart = new Am5DateValueXYChart(
            chartSeries,
            chartConfiguration,
            chartSettings
        );
    }

    setSocialTypes(selectedType: NetworkTypeEnum) {
        this.currentSocialType = selectedType;

        const summaryTrend = this.seriesMap.get(MADSocialSeriesTypesEnum.summaryTrend);

        this.dashboardSearchService.deregisterSeries([
            summaryTrend,
            ...this.summaryTotalVolumeCardSeries,
            ...this.summaryTotalImpressionCardSeries,
            ...this.summaryTotalEngagementCardSeries
        ]);

        this.setSummaryTrendIncludedMediaTypes(summaryTrend);

        this.dashboardSearchService.fulfillSeries([
            summaryTrend,
            ...this.summaryTotalVolumeCardSeries,
            ...this.summaryTotalImpressionCardSeries,
            ...this.summaryTotalEngagementCardSeries
        ]);
    }

    sentimentTrendChartStackTypeChanged(index: number): void {
        this.selectedSentimentTrendChartTabIndex = index;
        this.setupSentimentSeries();
        this.refreshClassToChartModelMap();
    }

    refreshClassToChartModelMap(): void {
        if (!this.classToChartModelMap) {
            return;
        }
        this.classToChartModelMap.set(this.summaryTrendLineChartClass, this.summaryTrendLineChart);
        this.classToChartModelMap.set(this.shareOfVoiceBySocialNetworkClass, this.shareOfVoiceBySocialNetwork);
        this.classToChartModelMap.set(this.sentimentTrendChartClass, this.sentimentTrendChart);
    }

    setSummaryTrendIncludedMediaTypes(summaryTrendSeries: ReportSeriesModel): void {
        summaryTrendSeries.mentions.filters.searchCriteria.query.includedMediaTypes = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.summaryTotalVolumeCardSeries.map(series =>
            series.mentions.filters.searchCriteria.query.includedMediaTypes = networkTypeToMediaTypeModelMap.get(this.currentSocialType)
        );
        this.summaryTotalImpressionCardSeries.map(series =>
            series.mentions.filters.searchCriteria.query.includedMediaTypes = networkTypeToMediaTypeModelMap.get(this.currentSocialType)
        );
        this.summaryTotalEngagementCardSeries.map(series =>
            series.mentions.filters.searchCriteria.query.includedMediaTypes = networkTypeToMediaTypeModelMap.get(this.currentSocialType)
        );

    }

    openUpsellDialog(): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                type: UpsellDialogType.social
            }
        };
        this.coreDialogService.open(UpsellInfoDialogComponent, io);
    }

    updateSummaryTrendLineChartNoData(event: boolean) {
        this.summaryTrendLineChartNoData = event;
    }

    updateSentimentTrendChartNoData(event: boolean) {
        this.sentimentTrendChartNoData = event;
    }

    public async exportChart(event: ChartExportEventModel, elementRef: HTMLElement, chartType?: ReportBlockTypeEnum) {
        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        const exportModel = this.prepareExportData(chartType);
        if (exportType === 'csv' || exportType === 'json') {
            this.chartExportService.exportData(exportType, exportModel);
        } else {
            await this.chartExportService.exportImageSnapDomWithOverrides(
                exportType,
                elementRef,
                exportModel.title,
                [ { selector: '.agpr-madSocial-exportMenu', style: { display: 'none' } } ]
            );
        }
    }
    private prepareExportData(chartType: ReportBlockTypeEnum): SocialChartExportModel {
        switch (chartType) {
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
                return {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.summary.shareOfVoiceBySocialNetwork'),
                    exportData: this.chartExportService.setExportDataForChartGroup(this.shareOfVoiceBySocialNetwork.charts),
                    exportLabelDefinitions: new Map<string, string>([
                        ['label', this.localizationService.transform( 'Social.export.label.mediaType')],
                        ['xValue_VOLUME0', this.localizationService.transform( 'Social.export.label.previousVolume')],
                        ['xValue_VOLUME1', this.localizationService.transform( 'Social.export.label.volume')],
                        ['xValue_IMPRESSIONS0', this.localizationService.transform( 'Social.export.label.previousReach')],
                        ['xValue_IMPRESSIONS1', this.localizationService.transform( 'Social.export.label.reach')],
                        ['xValue_ENGAGEMENTS0', this.localizationService.transform( 'Social.export.label.previousEngagements')],
                        ['xValue_ENGAGEMENTS1', this.localizationService.transform( 'Social.export.label.engagements')],
                    ])
                };
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                return {
                    title: this.localizationService.transform('Monitor.MAD.social.summaryTrend'),
                };
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                return {
                    title: this.localizationService.transform('Monitor.MAD.social.sentimentTrend'),
                };
            default:
                break;
        }
        return;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }
}
